import { useMemo, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ValueListsPage } from './settings/ValueListsPage';
import { ModulesPage } from './settings/ModulesPage';
import { UserPermissionsPage } from './settings/UserPermissionsPage';
import { PrintersPage } from './settings/PrintersPage';
import { BusinessPrintSettingsPage } from './settings/BusinessPrintSettingsPage';
import { FortnoxSettingsPage } from './settings/FortnoxSettingsPage';
import { WooCommerceSettingsPage } from './settings/WooCommerceSettingsPage';
import { ZettleSettingsPage } from './settings/ZettleSettingsPage';
import { Gs1SettingsPage } from './settings/Gs1SettingsPage';

type Tab = 'value-lists' | 'modules' | 'permissions' | 'printers' | 'print-settings' | 'fortnox' | 'woocommerce' | 'zettle' | 'gs1';

export function SettingsPage() {
  const { canRead } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryTab = searchParams.get('tab');
  const initialTab = useMemo<Tab>(() => {
    if (queryTab === 'modules' || queryTab === 'permissions' || queryTab === 'printers' || queryTab === 'print-settings' || queryTab === 'fortnox' || queryTab === 'woocommerce' || queryTab === 'zettle' || queryTab === 'gs1') {
      return queryTab;
    }
    return 'value-lists';
  }, [queryTab]);
  const [tab, setTab] = useState<Tab>(initialTab);

  const setActiveTab = (nextTab: Tab) => {
    setTab(nextTab);
    const next = new URLSearchParams(searchParams);
    next.set('tab', nextTab);
    setSearchParams(next, { replace: true });
  };

  if (!canRead('settings')) {
    return <Navigate to="/" replace />;
  }

  return (
    <div>
      <h1 className="mb-2 text-lg font-semibold">Inställningar</h1>
      <nav className="mb-3 flex gap-1 border-b border-gray-200">
        <button
          type="button"
          onClick={() => setActiveTab('value-lists')}
          className={`rounded-t px-2 py-1.5 text-sm ${
            tab === 'value-lists' ? 'bg-gray-100 font-medium' : 'hover:bg-gray-50'
          }`}
        >
          Värdelistor
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('modules')}
          className={`rounded-t px-2 py-1.5 text-sm ${
            tab === 'modules' ? 'bg-gray-100 font-medium' : 'hover:bg-gray-50'
          }`}
        >
          Moduler
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('permissions')}
          className={`rounded-t px-2 py-1.5 text-sm ${
            tab === 'permissions' ? 'bg-gray-100 font-medium' : 'hover:bg-gray-50'
          }`}
        >
          Behörigheter
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('printers')}
          className={`rounded-t px-2 py-1.5 text-sm ${
            tab === 'printers' ? 'bg-gray-100 font-medium' : 'hover:bg-gray-50'
          }`}
        >
          Skrivare
        </button>
        <button
          type="button"
          onClick={() => navigate('/settings/plan')}
          className="rounded-t px-2 py-1.5 text-sm hover:bg-gray-50"
        >
          Plan &amp; Fakturering
        </button>
        <button
          type="button"
          onClick={() => navigate('/settings/company')}
          className="rounded-t px-2 py-1.5 text-sm hover:bg-gray-50"
        >
          Företagsinformation
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('print-settings')}
          className={`rounded-t px-2 py-1.5 text-sm ${
            tab === 'print-settings' ? 'bg-gray-100 font-medium' : 'hover:bg-gray-50'
          }`}
        >
          Utskriftsinställningar
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('fortnox')}
          className={`rounded-t px-2 py-1.5 text-sm ${
            tab === 'fortnox' ? 'bg-gray-100 font-medium' : 'hover:bg-gray-50'
          }`}
        >
          Fortnox
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('woocommerce')}
          className={`rounded-t px-2 py-1.5 text-sm ${
            tab === 'woocommerce' ? 'bg-gray-100 font-medium' : 'hover:bg-gray-50'
          }`}
        >
          WooCommerce
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('zettle')}
          className={`rounded-t px-2 py-1.5 text-sm ${
            tab === 'zettle' ? 'bg-gray-100 font-medium' : 'hover:bg-gray-50'
          }`}
        >
          Zettle
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('gs1')}
          className={`rounded-t px-2 py-1.5 text-sm ${
            tab === 'gs1' ? 'bg-gray-100 font-medium' : 'hover:bg-gray-50'
          }`}
        >
          GS1
        </button>
      </nav>
      {tab === 'value-lists' && <ValueListsPage />}
      {tab === 'modules' && <ModulesPage />}
      {tab === 'permissions' && <UserPermissionsPage />}
      {tab === 'printers' && <PrintersPage />}
      {tab === 'print-settings' && <BusinessPrintSettingsPage />}
      {tab === 'fortnox' && <FortnoxSettingsPage />}
      {tab === 'woocommerce' && <WooCommerceSettingsPage />}
      {tab === 'zettle' && <ZettleSettingsPage />}
      {tab === 'gs1' && <Gs1SettingsPage />}
    </div>
  );
}
